import React, { useState, useEffect } from "react";
import {
  Table,
  Badge,
  Pagination,
  Container,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axios";

export default function UserApprovalRequests() {
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // 한 페이지당 항목 수
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserRequests = async () => {
      setLoading(true);
      try {
        const userString = sessionStorage.getItem("user"); // 세션 또는 로컬 스토리지에서 이메일 가져오기a

        if (!userString) {
          throw new Error("사용자 이메일 정보를 찾을 수 없습니다.");
        }

        const user = JSON.parse(userString); // JSON 문자열을 객체로 변환

        const response = await axiosInstance.get("/api/approval-request/list");
        const requestsData = Array.isArray(response.data)
          ? response.data
          : response.data.data; // 데이터 배열 추출

        // 사용자 필터링
        const userRequests = requestsData.filter(
          (request) => request.email === user.email
        );

        setApprovalRequests(userRequests || []);
      } catch (err) {
        console.error("Error fetching approval requests:", err);
        setError("결재 요청 데이터를 가져오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserRequests();
  }, []);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">요청</Badge>;
      case "FIRST_APPROVED":
        return <Badge bg="primary">1차 승인</Badge>;
      case "FINAL_APPROVED":
        return <Badge bg="success">최종 승인</Badge>;
      case "REJECTED":
        return <Badge bg="danger">반려</Badge>;
      default:
        return <Badge bg="secondary">알 수 없음</Badge>;
    }
  };

  const getCategory = (category) => {
    switch (category) {
      case "EXPENDITURE":
        return "지출";
      case "EMPLOYMENT":
        return "재직증명서";
      case "OTHER":
        return "기타";
      default:
        return "알 수 없음";
    }
  };

  // 페이지네이션 관련 계산
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = approvalRequests.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">로딩 중...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5 text-center">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h3 className="mb-4 text-center">내 결재 요청 내역</h3>
      {approvalRequests.length === 0 ? (
        <p className="text-center">결재 요청 내역이 없습니다.</p>
      ) : (
        <>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>제목</th>
                <th>구분</th>
                <th>상태</th>
                <th>생성일</th>
              </tr>
            </thead>
            <tbody>
              {currentRequests.map((request, index) => (
                <tr
                  key={request.approvalRequestId}
                  onClick={() =>
                    navigate(
                      `/approval-request/detail/${request.approvalRequestId}`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{request.title}</td>
                  <td>{getCategory(request.category)}</td>
                  <td>{renderStatusBadge(request.status)}</td>
                  <td>{formatDate(request.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* 페이지네이션 */}
          <Pagination className="justify-content-center mt-4">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {Array.from(
              { length: Math.ceil(approvalRequests.length / itemsPerPage) },
              (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={
                currentPage ===
                Math.ceil(approvalRequests.length / itemsPerPage)
              }
            />
            <Pagination.Last
              onClick={() =>
                handlePageChange(
                  Math.ceil(approvalRequests.length / itemsPerPage)
                )
              }
              disabled={
                currentPage ===
                Math.ceil(approvalRequests.length / itemsPerPage)
              }
            />
          </Pagination>
        </>
      )}
    </Container>
  );
}
